/**
 * The in-progress break sheet set, and how it survives the window closing.
// standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
 *
 * Preparing a hundred field-carrying sheets is an hour of typing, and until now
 * none of it existed anywhere until the operator pressed Print. Escape, the
 * backdrop and the Close button each threw the lot away without asking, and a
 * Windows update did the same without even that much ceremony.
 *
 * The draft is therefore written continuously to renderer storage, keyed by job,
 * rather than being held only in component state. Renderer storage is the same
 * place the review layout for a job already lives, so this adds no new IPC
 * surface and no new file for the main process to own — and a draft is exactly
 * the kind of thing that belongs to the workstation rather than to the batch.
 * A *printed* run is already durable in the data root; this covers the part that
 * was not.
 *
 * WHY A FIELD IS HIDDEN FOR THE WHOLE SET RATHER THAN PER SHEET OR PER TYPE
 *
 * The reason to withhold a value from print is a property of the field — an SSN
 * is an SSN on every sheet — not of one row. Toggling it a hundred times would
 * be the same data-entry problem this module exists to remove, so the choice is
 * made once and applied to every sheet in the set.
 *
 * The choice used to be filed under the document type it was made on, and held
 * field *names*. Both were wrong in the same direction, which is the dangerous
 * one. A set is explicitly allowed to mix types, so withholding an SSN on a
 * Claim left every Referral in the same set printing it. Worse, restoring a
 * draft whose type the job had since dropped moved the sheet onto a surviving
 * type and left the choice filed under the old one, where nothing would ever
 * look for it again — the sheet then printed every withheld value in the clear
 * with nothing on screen to say so. A rename did the same by name.
 *
 * So the withheld set belongs to the whole draft, and a field is matched by its
 * stable id *or* by its name. The id survives a rename; the name reaches the
 * same field on every other type in the set. Neither a remap, a rename, nor a
 * dropped type can turn a withheld value back into a printed one, and the only
 * thing that can is the operator clicking the control again.
 *
 * The rule everything below obeys: a value prints only when the code can prove
 * it was meant to. Anything it cannot resolve is withheld.
 *
 * WHY THE VALUES ARE FILED UNDER THE FIELD ID TOO
 *
 * Making the withheld choice rename-proof and leaving the values keyed by name
 * fixed half of one defect. A job that renames `SSN` to `Social Security Number`
 * keeps the field's id, so the choice followed it — and every value the operator
 * had typed did not. Reopening the dialog showed the banner saying the draft was
 * back above a hundred empty boxes, while the values themselves sat in storage
 * under a name nothing would ever ask for again, counting against the size cap
 * forever and keeping `draftHasWork` true.
 *
 * So a value is filed under the same key the printing decision is made on: the
 * stable id, or the folded name when a field has no id at all. Drafts written
 * under the old keys are remapped on the way in, against the job's own fields,
 * and a value whose field the job no longer has is dropped rather than carried.
 *
 * WHY THE REMAP IS DONE PER SHEET, AGAINST THE SHEET'S OWN TYPE
 *
 * A name only identifies a field within one document type. Resolving an older
 * draft's names against one flat map of every field of every type meant that a
 * name two types share — `Invoice Number` on an Invoice and on a Credit Memo —
 * resolved to whichever of them the job happened to list last. Every Invoice
 * sheet then had its typing moved onto the Credit Memo's field ids: the values
 * survived, so the dialog said the draft was back, and it said so above empty
 * boxes and printed a blank sheet.
 *
 * So each sheet is resolved against the fields of its own document type, and the
 * flat map is the fallback for the one case it is right for — a sheet whose type
 * the job does not know at all, which is landed on a surviving type afterwards.
 * That surviving type is the job's first, so its fields are asked first: a name
 * the first type shares with a later one resolved to the later one's id, and the
 * sheet came back on the first type above an empty box.
 *
 * WHY ARCHIVED TYPES ARE READ TOO
 *
 * A type archived between sessions is still the job's, and a sheet filled in on
 * it is still what the operator typed. Resolved against live types only, its
 * values matched nothing and were dropped, and the blank sheet landed on the
 * first type and printed under that name. So a draft is read against every type
 * the job has, live first, and a filled-in sheet on an archived type comes back
 * on it, waiting for the operator to choose a live type (`restoreBreakSheetDraft`).
 */

/**
 * The most any one field value may be — in the draft, in the request, and in the
 * stored record, which all use the same number so none of them can quietly hold
 * something another one will refuse. A break sheet value is an identifier or a
 * name, not a paragraph; a free-text column imported from a table is what makes
 * this a bound rather than a formality.
 */
export const MAX_FIELD_VALUE_LENGTH = 200;

/**
 * The most one job's draft may occupy. Renderer storage is a single origin quota
 * of roughly five megabytes shared with everything else the workstation keeps
 * there, so a draft that grew without limit would not fail alone — it would take
 * the review layout and every other job's draft down with it.
 */
const MAX_DRAFT_BYTES = 768 * 1024;

/** Drafts older than this are last year's work and are swept on the way in. */
const MAX_DRAFT_AGE_MILLISECONDS = 14 * 24 * 60 * 60 * 1000;

/**
 * How many jobs may keep a draft at once. Nothing deletes a draft when its job
 * is deleted — the dialog never hears about that — so without a ceiling the
 * quota is consumed monotonically and forever by jobs that no longer exist.
 */
const MAX_DRAFT_JOBS = 8;

/**
 * One prepared sheet as the operator is editing it, before anything is printed.
 *
 * `values` is keyed by the field key — see `fieldValueKey` — never by the name
 * showing on screen, because the name is the one part of a field a job is free
 * to change under a draft that is already written.
 */
export type DraftSheet = {
  documentTypeId: string;
  values: Record<string, string>;
};

/**
 * A field as the dialog knows it: the stable id the job carries for it, and the
 * name it happens to have today. Both are needed — see the header.
 */
export type WithholdableField = {
  readonly id: string;
  readonly name: string;
};

/**
 * The fields whose value is stored against the sheet's identifier and still
 * fills the field when the sheet is read, but is not printed in the clear on
 * paper that sits in a stack on a desk.
 *
 * Two lists rather than one because they answer different questions. `fieldIds`
 * follows a field through a rename. `fieldNames` reaches the same field on every
 * other document type in the set, and is never pruned, because the only thing
 * that could shorten it is a guess about which types the operator meant.
 */
export type WithheldFields = {
  readonly fieldIds: readonly string[];
  readonly fieldNames: readonly string[];
};

export const noWithheldFields: WithheldFields = { fieldIds: [], fieldNames: [] };

/** Names are matched the way an operator reads them, not the way they are typed. */
const withholdKey = (name: string): string => name.trim().toLowerCase();

/** Whether this field has enough identity for a printing decision to be made about it. */
const identifiable = (field: WithholdableField): boolean =>
  field.id.trim().length > 0 || withholdKey(field.name).length > 0;

export const fieldIsWithheld = (withheld: WithheldFields, field: WithholdableField): boolean => {
  if (!identifiable(field)) return true;
  if (field.id.trim().length > 0 && withheld.fieldIds.includes(field.id)) return true;
  const key = withholdKey(field.name);
  return key.length > 0 && withheld.fieldNames.includes(key);
};

/**
 * Whether this field may be printed. Stated separately from `fieldIsWithheld`
 * so the affirmative is the thing the print path asks for: it has to prove the
 * value is printable, not fail to prove that it is not.
 */
export const fieldMayBePrinted = (withheld: WithheldFields, field: WithholdableField): boolean =>
  identifiable(field) && !fieldIsWithheld(withheld, field);

/**
 * Turn the choice on or off. Turning it on records the id and the name, so the
 * field stays withheld through a rename and every same-named field in the set is
 * covered. Turning it off removes both, because the operator asking for a value
 * to be printed is the one thing allowed to un-withhold it.
 */
export const toggleWithheldField = (
  withheld: WithheldFields,
  field: WithholdableField,
): WithheldFields => {
  const key = withholdKey(field.name);
  if (fieldIsWithheld(withheld, field)) {
    return {
      fieldIds: withheld.fieldIds.filter((id) => id !== field.id),
      fieldNames: withheld.fieldNames.filter((name) => name !== key),
    };
  }
  return {
    fieldIds:
      field.id.trim().length > 0 && !withheld.fieldIds.includes(field.id)
        ? [...withheld.fieldIds, field.id]
        : [...withheld.fieldIds],
    fieldNames:
      key.length > 0 && !withheld.fieldNames.includes(key)
        ? [...withheld.fieldNames, key]
        : [...withheld.fieldNames],
  };
};

export const hasWithheldChoice = (withheld: WithheldFields): boolean =>
  withheld.fieldIds.length > 0 || withheld.fieldNames.length > 0;

/**
 * The key one field's typed value is filed under.
 *
 * The stable id, because that is the only part of a field that a job renaming it
 * does not change, and a renamed field with a blank box beside it is a draft the
 * operator has to type again. A field with no id at all falls back to its folded
 * name, which is the same fallback the withheld choice makes for the same reason.
 */
export const fieldValueKey = (field: WithholdableField): string =>
  field.id.trim().length > 0 ? field.id.trim() : withholdKey(field.name);

/** What the operator typed into this field, or nothing. */
export const sheetValue = (sheet: DraftSheet, field: WithholdableField): string =>
  sheet.values[fieldValueKey(field)] ?? '';

/** The same sheet with one field's value replaced. */
export const withSheetValue = (
  sheet: DraftSheet,
  field: WithholdableField,
  value: string,
): DraftSheet => ({
  ...sheet,
  values: { ...sheet.values, [fieldValueKey(field)]: value },
});

/**
 * Drop ids for fields the job no longer has, so a draft carried across months of
 * schema learning does not grow a list of ids that can never match anything.
 * Names are deliberately left alone: an id that is gone is a field that is gone,
 * but a name is how the same field is recognised on every other type, and
 * pruning one would be un-withholding a value on a guess.
 */
export const pruneWithheldFields = (
  withheld: WithheldFields,
  knownFieldIds: ReadonlySet<string>,
): WithheldFields => ({
  fieldIds: withheld.fieldIds.filter((id) => knownFieldIds.has(id)),
  fieldNames: [...withheld.fieldNames],
});

/**
 * Read the shape drafts were written in before the choice became set-wide: field
 * names, filed under the document type they were chosen on. Every name is kept
 * and every type key is discarded, which withholds strictly more than the old
 * draft did. That is the only safe direction to migrate in — the alternative is
 * printing a value the operator had already asked to be kept off the paper.
 */
export const migrateLegacyHiddenFields = (legacy: unknown): WithheldFields => {
  if (typeof legacy !== 'object' || legacy === null) return noWithheldFields;
  const fieldNames: string[] = [];
  for (const names of Object.values(legacy as Record<string, unknown>)) {
    if (!Array.isArray(names)) continue;
    for (const name of names) {
      if (typeof name !== 'string') continue;
      const key = withholdKey(name);
      if (key.length > 0 && !fieldNames.includes(key)) fieldNames.push(key);
    }
  }
  return { fieldIds: [], fieldNames };
};

/**
 * Version 2 moved the withheld choice off the document type; version 3 moved the
 * typed values off the field name. Both earlier shapes are migrated on the way
 * in; an earlier build reading a later draft sees a shape it does not know and
 * treats it as absent, which loses typing but cannot print a withheld value.
 */
export type BreakSheetDraft = {
  readonly version: 3;
  readonly savedAt: string;
  readonly sheets: DraftSheet[];
  readonly withheldFields: WithheldFields;
};

export const breakSheetDraftKey = (jobId: string): string => `cpv6-break-sheet-draft:${jobId}`;

const draftKeyPrefix = 'cpv6-break-sheet-draft:';

/** A sheet the operator has not touched: nothing typed, so nothing to lose. */
export const sheetIsEmpty = (sheet: DraftSheet): boolean =>
  Object.values(sheet.values).every((value) => value.trim().length === 0);

/**
 * Whether the draft holds work worth keeping. One untouched sheet is the state
 * the dialog opens in, so it is not a draft and must not raise a guard.
 */
export const draftHasWork = (sheets: readonly DraftSheet[]): boolean =>
  sheets.length > 1 || sheets.some((sheet) => !sheetIsEmpty(sheet));

/**
 * A cheap measure of how much a draft would occupy, without serialising it.
 *
 * The dialog uses it to decide whether a save that has already failed for space
 * is worth attempting again. Serialising the whole set to find that out is the
 * cost being avoided: a full set is hundreds of kilobytes and the answer would
 * be the same on every keystroke until the operator removed something.
 */
export const draftFootprint = (sheets: readonly DraftSheet[]): number => {
  let total = 0;
  for (const sheet of sheets) {
    total += sheet.documentTypeId.length + 16;
    for (const [name, value] of Object.entries(sheet.values))
      total += name.length + value.length + 8;
  }
  return total;
};

const isDraftSheet = (value: unknown): value is DraftSheet => {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<DraftSheet>;
  if (typeof candidate.documentTypeId !== 'string') return false;
  if (typeof candidate.values !== 'object' || candidate.values === null) return false;
  return Object.values(candidate.values).every((entry) => typeof entry === 'string');
};

const readWithheldFields = (value: unknown): WithheldFields => {
  if (typeof value !== 'object' || value === null) return noWithheldFields;
  const candidate = value as Partial<WithheldFields>;
  const strings = (entries: unknown): string[] =>
    Array.isArray(entries)
      ? entries.filter((entry): entry is string => typeof entry === 'string')
      : [];
  return {
    fieldIds: strings(candidate.fieldIds),
    fieldNames: strings(candidate.fieldNames)
      .map(withholdKey)
      .filter((name) => name.length > 0),
  };
};

/**
 * A document type as the draft reader needs it: the id its sheets carry, and the
 * fields a sheet of that type files its values under.
 *
 * The fields are grouped by type rather than handed over flat because a field
 * name means nothing on its own — see the header.
 */
export type DraftDocumentType = {
  readonly id: string;
  readonly fields: readonly WithholdableField[];
};

/** The keys one document type's values are filed under, and the names that reach them. */
type FieldKeys = {
  readonly live: ReadonlySet<string>;
  readonly byName: ReadonlyMap<string, string>;
};

const fieldKeys = (fields: readonly WithholdableField[]): FieldKeys => ({
  live: new Set(fields.map(fieldValueKey)),
  byName: new Map(fields.map((field) => [withholdKey(field.name), fieldValueKey(field)])),
});

/**
 * Move a draft's values onto the keys this version files them under, and drop
 * the ones the job can no longer place.
 *
 * Both halves need the job's own fields, which is why they happen here rather
 * than inside the parse: a value stored under `SSN` belongs to whichever live
 * field carries that name today, and a value under a name no live field carries
 * belongs to nothing. Leaving those behind is not free — they are the reason a
 * draft an operator emptied still counted against the size cap and still read as
 * work in progress.
 *
 * A sheet is resolved against its own document type's fields. Only a sheet whose
 * type is not among `types` falls back to every field the job knows, because
 * that sheet is about to be landed on a surviving type and a value under a name
 * some type still carries is better evidence than nothing. The type it lands on
 * is the first one (`landSheetsOnKnownTypes`), so where several types carry the
 * same name, the first type's field is the one the value is filed under.
 *
 * With no types to resolve against, every key is left exactly as it was: an empty
 * list means "the job's fields are not known here", not "the job has none".
 */
const resolveValueKeys = (
  sheets: DraftSheet[],
  types: readonly DraftDocumentType[],
  alreadyKeyed: boolean,
): DraftSheet[] => {
  const everyField = types.flatMap((type) => [...type.fields]);
  if (everyField.length === 0) return sheets;
  const byType = new Map(types.map((type) => [type.id, fieldKeys(type.fields)]));
  const anywhere = fieldKeys(everyField);
  // The first type's names win over a later type's, because the first type is
  // where an orphaned sheet is landed; a Map built from entries keeps the last.
  const landing = types.length > 0 ? byType.get(types[0].id) : undefined;
  const orphaned: FieldKeys = landing
    ? { live: anywhere.live, byName: new Map([...anywhere.byName, ...landing.byName]) }
    : anywhere;
  return sheets.map((sheet) => {
    const keys = byType.get(sheet.documentTypeId) ?? orphaned;
    const values: Record<string, string> = {};
    for (const [key, value] of Object.entries(sheet.values)) {
      const resolved = keys.live.has(key)
        ? key
        : alreadyKeyed
          ? null
          : (keys.byName.get(withholdKey(key)) ?? null);
      if (resolved !== null) values[resolved] = value;
    }
    return { ...sheet, values };
  });
};

/**
 * Read a stored draft back.
 *
 * Storage is shared with every other version of the product this workstation has
 * ever run, so nothing here trusts its own shape: anything that does not parse
 * into the current one is treated as absent rather than crashing the dialog an
 * operator opened to get their work back.
 *
 * `types` is every document type the job currently has, each with its own
 * fields, the live ones first (the first is where an orphaned sheet lands). It is
 * what an older draft's name-keyed values are remapped through, and what an
 * orphaned value is recognised by. Callers that only want to know whether a
 * draft exists can leave it out.
 */
export const readBreakSheetDraft = (
  storage: Storage,
  jobId: string,
  types: readonly DraftDocumentType[] = [],
): BreakSheetDraft | null => {
  let raw: string | null;
  try {
    raw = storage.getItem(breakSheetDraftKey(jobId));
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as {
      version?: unknown;
      savedAt?: unknown;
      sheets?: unknown;
      withheldFields?: unknown;
      hiddenFields?: unknown;
    };
    if (parsed.version !== 1 && parsed.version !== 2 && parsed.version !== 3) return null;
    if (!Array.isArray(parsed.sheets)) return null;
    const sheets = parsed.sheets.filter(isDraftSheet);
    if (sheets.length === 0) return null;
    return {
      version: 3,
      savedAt: typeof parsed.savedAt === 'string' ? parsed.savedAt : '',
      sheets: resolveValueKeys(sheets, types, parsed.version === 3),
      withheldFields:
        parsed.version === 1
          ? migrateLegacyHiddenFields(parsed.hiddenFields)
          : readWithheldFields(parsed.withheldFields),
    };
  } catch {
    return null;
  }
};

/**
 * Write the draft. Returns a message when it could not be stored, because a
 * draft that silently stopped saving is the defect this module was written to
 * remove — the operator has to be told to print what they have.
 *
 * Values are cut to `MAX_FIELD_VALUE_LENGTH` here as a backstop rather than as
 * the place the bound is applied: the dialog and the table import already hold
 * to the same number, so what comes back is what was typed. A whole draft that
 * is still over budget after that is refused rather than attempted, because the
 * failure mode of attempting it is evicting somebody else's data.
 */
export const writeBreakSheetDraft = (
  storage: Storage,
  jobId: string,
  sheets: readonly DraftSheet[],
  withheldFields: WithheldFields,
): string | null => {
  const draft: BreakSheetDraft = {
    version: 3,
    savedAt: new Date().toISOString(),
    sheets: sheets.map((sheet) => ({
      documentTypeId: sheet.documentTypeId,
      values: Object.fromEntries(
        Object.entries(sheet.values).map(([name, value]) => [
          name,
          value.slice(0, MAX_FIELD_VALUE_LENGTH),
        ]),
      ),
    })),
    withheldFields: {
      fieldIds: [...withheldFields.fieldIds],
      fieldNames: [...withheldFields.fieldNames],
    },
  };
  const serialised = JSON.stringify(draft);
  if (serialised.length > MAX_DRAFT_BYTES) {
    return 'This set is too large to keep a draft of on this computer, so closing the window will lose it. Print the sheets you have.';
  }
  try {
    storage.setItem(breakSheetDraftKey(jobId), serialised);
    return null;
  } catch {
    return 'This draft could not be saved on this computer, so closing the window will lose it. Print the sheets you have.';
  }
};

/**
 * Whether a draft is still on this workstation after a save attempt.
 *
 * A refusal does not remove what is already stored, and reading it as though it
 * did cost an operator the one thing this module promises. The dialog only
 * bothers writing when there is work to write *or* something stored to
 * overwrite; a `QuotaExceededError` — reachable, because the five megabyte origin
 * quota is shared with everything else the renderer keeps — lowered the second
 * of those. Deleting every sheet back to one blank one after that wrote nothing
 * at all, and the large draft the operator had deliberately thrown away came
 * straight back the next time they opened the window.
 *
 * Only clearing the draft lowers it, because only clearing the draft removes it.
 */
export const draftRemainsStored = (wasStored: boolean, saveWarning: string | null): boolean =>
  saveWarning === null || wasStored;

export const clearBreakSheetDraft = (storage: Storage, jobId: string): void => {
  try {
    storage.removeItem(breakSheetDraftKey(jobId));
  } catch {
    // A draft that cannot be removed is stale data, not a failed action worth
    // stopping the operator over; the next save overwrites it.
  }
};

/**
 * Sweep drafts that will never be opened again.
 *
 * Nothing tells this dialog that a job was deleted, so a draft outlives its job
 * and the origin quota is consumed monotonically. Age and count are the two
 * facts available here without asking the main process for the job list, and
 * both are safe: a draft is a convenience with a durable printed run on the
 * other side of it, never the only copy of anything that has been printed.
 */
export const pruneBreakSheetDrafts = (
  storage: Storage,
  options: { readonly keepJobId?: string; readonly now?: number } = {},
): number => {
  const now = options.now ?? Date.now();
  const keepKey = options.keepJobId === undefined ? null : breakSheetDraftKey(options.keepJobId);
  let entries: { key: string; savedAt: number }[];
  // Whether the job being kept actually holds one of the places. It was assumed
  // to, and the assumption cost somebody else's work: merely opening this dialog
  // on a ninth job — which has no draft, and never gets one unless the operator
  // types something — reserved a place for it anyway and swept the oldest of the
  // eight real drafts to make room for nothing.
  let keeping = false;
  try {
    keeping = keepKey !== null && storage.getItem(keepKey) !== null;
  } catch {
    keeping = false;
  }
  try {
    entries = [];
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key === null || !key.startsWith(draftKeyPrefix) || key === keepKey) continue;
      let savedAt = 0;
      try {
        const parsed = JSON.parse(storage.getItem(key) ?? '') as { savedAt?: unknown };
        savedAt = typeof parsed.savedAt === 'string' ? Date.parse(parsed.savedAt) : 0;
      } catch {
        // Unreadable is as good a reason to sweep it as stale.
      }
      entries.push({ key, savedAt: Number.isNaN(savedAt) ? 0 : savedAt });
    }
  } catch {
    return 0;
  }

  const stale = entries.filter((entry) => now - entry.savedAt > MAX_DRAFT_AGE_MILLISECONDS);
  const fresh = entries
    .filter((entry) => !stale.includes(entry))
    .sort((a, b) => b.savedAt - a.savedAt);
  // A kept draft that exists holds one of the places, so it is counted here even
  // though it was never a candidate for removal. One that does not exist holds
  // nothing.
  const allowance = keeping ? MAX_DRAFT_JOBS - 1 : MAX_DRAFT_JOBS;
  const surplus = fresh.slice(Math.max(0, allowance));

  let removed = 0;
  for (const entry of [...stale, ...surplus]) {
    try {
      storage.removeItem(entry.key);
      removed += 1;
    } catch {
      // Nothing to do about a key that will not go; the next sweep tries again.
    }
  }
  return removed;
};

/** "2 minutes ago", for telling an operator how old the draft they got back is. */
export const describeDraftAge = (savedAt: string, now: number = Date.now()): string => {
  const when = new Date(savedAt).getTime();
  if (Number.isNaN(when)) return '';
  const minutes = Math.floor((now - when) / 60_000);
  if (minutes < 1) return 'a moment ago';
  if (minutes === 1) return 'a minute ago';
  if (minutes < 60) return `${minutes} minutes ago`;
  const hours = Math.floor(minutes / 60);
  if (hours === 1) return 'an hour ago';
  if (hours < 24) return `${hours} hours ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? 'yesterday' : `${days} days ago`;
};

// ---------------------------------------------------------------------------
// Turning the draft into a print request
// ---------------------------------------------------------------------------

/** A document type, with the fields that can be written on a sheet already chosen. */
export type PrintableDocumentType = {
  readonly id: string;
  readonly name: string;
  readonly fields: readonly WithholdableField[];
};

/**
 * One field on its way to the printer. `printed` is always stated: the whole
 * defect this replaces was a flag that could go missing between the choice and
 * the page, so there is no such thing here as a field that did not say.
 */
export type PreparedSheetField = {
  readonly label: string;
  readonly value: string;
  readonly printed: boolean;
};

export type PreparedSheet = {
  readonly documentType: string | null;
  readonly fields: PreparedSheetField[];
};

/**
 * Put every sheet whose document type is not among `typeIds` on the first one.
 *
 * A sheet can point at a type the dialog does not offer: it was made while the
 * job had no types yet, or its type was archived or merged since. The dialog
 * shows such a sheet with the first type's fields, so the operator types into
 * those — and the sheet, still naming the missing type, then printed with no
 * document type and no fields while the draft was cleared as done. Landing it
 * on the type its boxes already show makes what is printed what was typed.
 *
 * `onlyEmpty` is for the open dialog: there a sheet whose type vanished while it
 * was being filled in keeps that type, so its values are not printed under the
 * first type's name with none of its fields. It is refused at print
 * (`sheetTypeProblem`) until the operator chooses its type.
 *
 * `jobTypeIds` narrows that for a restored draft: a filled-in sheet keeps its type
 * only when the job still has it, archived. One whose id the job does not know at
 * all — "" from a draft written before typing saved the type — was typed into
 * the first type's boxes, so it lands there.
 *
 * Returns the same array when nothing moves, so a caller can use it in an
 * effect without rendering again. With no types there is nowhere to land, and
 * the sheets are left as they are.
 */
export const landSheetsOnKnownTypes = (
  sheets: DraftSheet[],
  typeIds: readonly string[],
  { onlyEmpty = false, jobTypeIds }: { onlyEmpty?: boolean; jobTypeIds?: readonly string[] } = {},
): DraftSheet[] => {
  if (typeIds.length === 0) return sheets;
  const known = new Set(typeIds);
  const inJob = jobTypeIds === undefined ? null : new Set(jobTypeIds);
  const waits = (sheet: DraftSheet) =>
    onlyEmpty && !sheetIsEmpty(sheet) && (inJob === null || inJob.has(sheet.documentTypeId));
  const moves = (sheet: DraftSheet) => !known.has(sheet.documentTypeId) && !waits(sheet);
  if (!sheets.some(moves)) return sheets;
  return sheets.map((sheet) => (moves(sheet) ? { ...sheet, documentTypeId: typeIds[0] } : sheet));
};

/** What one sheet can legibly show; the main process enforces the same bound. */
export const MAX_FIELDS_PER_SHEET = 10;

/**
 * The fields an operator can actually write on a printed sheet.
 *
 * A line-items field is a table of billable rows read off the document; nobody
 * hand-types one onto a separator sheet, so offering it is an empty box that
 * can never be filled and a row that can never be printed.
 */
export const printableSheetFields = <TField extends { readonly kind: string }>(
  fields: readonly TField[],
): TField[] =>
  fields
    .filter((field) => !field.kind.toLowerCase().includes('line-items'))
    .slice(0, MAX_FIELDS_PER_SHEET);

/**
 * A sheet moved from a type the job no longer offers onto the type the operator
 * chose for it, its values carried across by field name (a field id belongs to
 * one type only). A value whose field has no namesake on the chosen type is left
 * behind; with the old type's fields unknown, nothing can be matched.
 *
 * `to` holds the chosen type's printable fields only (`printableSheetFields`): a
 * value carried past the field cap or into a line-items field has no box on
 * screen and never prints, yet keeps the sheet counting as work.
 */
export const carrySheetToType = (
  sheet: DraftSheet,
  from: DraftDocumentType | undefined,
  to: DraftDocumentType,
): DraftSheet => {
  const target = fieldKeys(to.fields).byName;
  const values: Record<string, string> = {};
  for (const field of from?.fields ?? []) {
    const value = sheetValue(sheet, field);
    const key = target.get(withholdKey(field.name));
    if (key !== undefined && value.length > 0 && values[key] === undefined) values[key] = value;
  }
  return { documentTypeId: to.id, values };
};

/**
 * What was typed on a sheet, by the names of the fields of `type`: for a sheet
 * waiting for a type, whose boxes are gone, so the operator still sees what it
 * holds before choosing one.
 */
export const sheetValuesByName = (
  sheet: DraftSheet,
  type: DraftDocumentType,
): { readonly name: string; readonly value: string }[] =>
  type.fields
    .map((field) => ({ name: field.name, value: sheetValue(sheet, field) }))
    .filter((entry) => entry.value.trim().length > 0);

/** A document type as a restored draft needs it: its fields, and whether it is archived. */
export type RestorableDocumentType = DraftDocumentType & { readonly archived: boolean };

/**
 * Read a job's draft back as the dialog opens on it.
 *
 * Read against live types and then archived ones, so a sheet filled in on a type
 * archived since keeps its type and its values: it comes back waiting for a type
 * (the dialog's "Choose a document type"), and printing is refused until it has
 * one. Empty sheets, and filled-in ones on a type the job does not know at all,
 * land on the first live type. The withheld choice is pruned against live
 * fields only; its names still reach every type.
 */
export const restoreBreakSheetDraft = (
  storage: Storage,
  jobId: string,
  types: readonly RestorableDocumentType[],
): BreakSheetDraft | null => {
  const live = types.filter((type) => !type.archived);
  const stored = readBreakSheetDraft(storage, jobId, [
    ...live,
    ...types.filter((type) => type.archived),
  ]);
  if (!stored) return null;
  const liveFieldIds = new Set(live.flatMap((type) => type.fields).map((field) => field.id));
  return {
    ...stored,
    sheets: landSheetsOnKnownTypes(
      stored.sheets,
      live.map((type) => type.id),
      {
        onlyEmpty: true,
        jobTypeIds: types.map((type) => type.id),
      },
    ),
    withheldFields: pruneWithheldFields(stored.withheldFields, liveFieldIds),
  };
};

/**
 * Why this set cannot be printed as it stands, or null when it can.
 *
 * The last guard before a run is created: a sheet whose document type is not
 * among the ones shown would print with no type and none of its values, and
 * creating the run clears the draft that holds them.
 */
export const sheetTypeProblem = (
  sheets: readonly DraftSheet[],
  typeIds: readonly string[],
): string | null => {
  const known = new Set(typeIds);
  const index = sheets.findIndex((sheet) => !known.has(sheet.documentTypeId));
  return index < 0
    ? null
    : `Sheet ${index + 1} has no document type this job still has, so its values would not print. Choose a document type for it and try again.`;
};

/**
 * Build the request the main process is given.
 *
 * This is the one place the operator's choice becomes a boolean on a wire, so it
 * is the one place that has to be right. Every field states whether it prints,
 * and a field whose identity cannot be established states `false`.
 */
export const prepareSheetsForPrinting = (
  sheets: readonly DraftSheet[],
  types: readonly PrintableDocumentType[],
  withheld: WithheldFields,
): PreparedSheet[] => {
  const byId = new Map(types.map((type) => [type.id, type]));
  return sheets.map((sheet) => {
    const type = byId.get(sheet.documentTypeId);
    const fields = (type?.fields ?? [])
      .map<PreparedSheetField>((field) => ({
        label: field.name,
        value: sheetValue(sheet, field).slice(0, MAX_FIELD_VALUE_LENGTH),
        printed: fieldMayBePrinted(withheld, field),
      }))
      .filter((field) => field.value.trim().length > 0);
    return { documentType: type?.name ?? null, fields };
  });
};

/**
 * Creating a run and opening it are two different facts, and conflating them
 * cost an operator two hundred sheets.
 *
 * The run is created first and is durable the moment it exists — it is in the
 * run history and reprintable from there. Opening it needs a PDF handler on the
 * workstation, which is a separate thing to be missing. When both lived in one
 * `try`, a workstation with no handler threw on the open, skipped the success
 * path entirely, cleared no draft and raised no toast, and the operator pressed
 * Print again and minted a second two-hundred-sheet run.
 *
 * So the caller is told what was made and, separately, whether it could be
 * shown. A created run always clears the draft, whatever happened after.
 */
export type BreakSheetPrintOutcome<TRun> =
  | { readonly created: false; readonly error: string }
  | { readonly created: true; readonly run: TRun; readonly openError: string | null };

const messageFor = (reason: unknown, fallback: string): string =>
  reason instanceof Error && reason.message.trim().length > 0 ? reason.message : fallback;

export const printAndOpenBreakSheets = async <TRun>(
  createRun: () => Promise<TRun>,
  openRun: (run: TRun) => Promise<void>,
): Promise<BreakSheetPrintOutcome<TRun>> => {
  let run: TRun;
  try {
    run = await createRun();
  } catch (reason) {
    return {
      created: false,
      error: messageFor(reason, 'The break sheets could not be created.'),
    };
  }
  try {
    await openRun(run);
    return { created: true, run, openError: null };
  } catch (reason) {
    return {
      created: true,
      run,
      openError: messageFor(
        reason,
        'The sheets were created but could not be opened for printing. They are in the list below - open them from there. Do not create them again.',
      ),
    };
  }
};
